import type { WavetablePreset } from "../../../../src/lib/audio";
import {
  WAVETABLE_PRESETS,
  cloneWavetablePreset,
  normalizeWavetablePreset,
} from "../../../../src/lib/audio";

/** Wavetable bank shelves — parallel to subtractive PresetCategoryId. */
export const WT_PRESET_CATEGORIES = [
  { id: "basic", label: "Basic" },
  { id: "pads", label: "Pads" },
  { id: "bass", label: "Bass" },
  { id: "leads", label: "Leads" },
  { id: "keys", label: "Keys" },
  { id: "experimental", label: "Experimental" },
  { id: "user", label: "User" },
] as const;

export type WtPresetCategoryId = typeof WT_PRESET_CATEGORIES[number]["id"];

export type NamedWavetablePreset = {
  id: string;
  name: string;
  builtin?: boolean;
  category: WtPresetCategoryId;
  tags?: string[];
  preset: WavetablePreset;
};

const BUILTIN_META: Array<{
  id: string;
  category: WtPresetCategoryId;
  tags: string[];
}> = [
  { id: "builtin-wt-wide-supersaw", category: "pads", tags: ["pad", "supersaw", "wide", "unison7"] },
  { id: "builtin-wt-analog-pwm-pad", category: "pads", tags: ["pad", "analog", "pwm", "bvker"] },
  { id: "builtin-wt-growl-bass", category: "bass", tags: ["bass", "growl", "sync", "mono"] },
  { id: "builtin-wt-glass-keys", category: "keys", tags: ["keys", "glass", "pluck"] },
  { id: "builtin-wt-fm-metallic", category: "experimental", tags: ["fm", "metallic", "bandpass"] },
  { id: "builtin-wt-hyper-lead", category: "leads", tags: ["lead", "hyper", "unison"] },
  { id: "builtin-wt-soft-ambient", category: "pads", tags: ["ambient", "wide", "unison9", "krc"] },
  { id: "builtin-wt-distorted-stab", category: "experimental", tags: ["stab", "distort", "noise"] },
  { id: "builtin-wt-mono-pwm-bass", category: "bass", tags: ["bass", "pwm", "mono", "sub"] },
  { id: "builtin-wt-krc-explore-pad", category: "pads", tags: ["pad", "krc", "explore"] },
];

export function createDefaultWavetableBank(): NamedWavetablePreset[] {
  return WAVETABLE_PRESETS.map((preset, i) => {
    const meta = BUILTIN_META[i]!;
    return {
      id: meta.id,
      name: preset.name,
      builtin: true,
      category: meta.category,
      tags: [...meta.tags],
      preset: cloneWavetablePreset(preset),
    };
  });
}

export function isWtPresetCategoryId(value: unknown): value is WtPresetCategoryId {
  return typeof value === "string" && WT_PRESET_CATEGORIES.some(c => c.id === value);
}

export function wtCategoryLabel(id: WtPresetCategoryId): string {
  return WT_PRESET_CATEGORIES.find(c => c.id === id)?.label ?? id;
}

export function wtCategorySortIndex(id: WtPresetCategoryId): number {
  const idx = WT_PRESET_CATEGORIES.findIndex(c => c.id === id);
  return idx >= 0 ? idx : WT_PRESET_CATEGORIES.length;
}

export function newWavetablePresetId(): string {
  return `user-wt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function sanitizeWtPresetCategory(
  value: unknown,
  fallback: WtPresetCategoryId = "user",
): WtPresetCategoryId {
  return isWtPresetCategoryId(value) ? value : fallback;
}

export function sanitizeWtPresetTags(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const tags = raw
    .filter((t): t is string => typeof t === "string")
    .map(t => t.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, 12);
  return tags.length > 0 ? [...new Set(tags)] : undefined;
}

/** Lightweight shape check for import JSON (normalization fills the rest). */
export function isWavetablePreset(value: unknown): value is WavetablePreset {
  if (!value || typeof value !== "object") {
    return false;
  }
  const preset = value as WavetablePreset;
  return Boolean(preset.osc1 && preset.osc2 && preset.filter && preset.ampEnv);
}

function sanitizeTags(raw: unknown): string[] | undefined {
  return sanitizeWtPresetTags(raw);
}

export function sanitizeWavetableBank(raw: unknown): NamedWavetablePreset[] {
  const factory = createDefaultWavetableBank();
  if (!Array.isArray(raw)) {
    return factory;
  }
  const byId = new Map<string, NamedWavetablePreset>();
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const entry = item as Partial<NamedWavetablePreset>;
    if (typeof entry.id !== "string" || !entry.id) {
      continue;
    }
    const preset = normalizeWavetablePreset(entry.preset);
    byId.set(entry.id, {
      id: entry.id,
      name: typeof entry.name === "string" && entry.name.trim() ? entry.name.trim() : preset.name,
      builtin: !!entry.builtin,
      category: isWtPresetCategoryId(entry.category) ? entry.category : "user",
      ...(sanitizeTags(entry.tags) ? { tags: sanitizeTags(entry.tags) } : {}),
      preset,
    });
  }
  const out: NamedWavetablePreset[] = [];
  for (const factoryEntry of factory) {
    const user = byId.get(factoryEntry.id);
    if (user) {
      out.push({
        ...factoryEntry,
        name: user.name || factoryEntry.name,
        category: user.category,
        tags: user.tags ?? factoryEntry.tags,
        preset: user.preset,
        builtin: true,
      });
      byId.delete(factoryEntry.id);
    } else {
      out.push(factoryEntry);
    }
  }
  for (const user of byId.values()) {
    if (!user.builtin) {
      out.push({ ...user, builtin: false, category: user.category || "user" });
    }
  }
  return out;
}

/**
 * Import / open project: file bank is base; keep non-builtin session presets
 * the file doesn't include (by id). Imported id wins on collision.
 */
export function mergeWavetableBanksPreservingUser(
  imported: readonly NamedWavetablePreset[] | undefined,
  existing: readonly NamedWavetablePreset[] | undefined,
): NamedWavetablePreset[] {
  const base = sanitizeWavetableBank(imported ?? createDefaultWavetableBank());
  if (!existing?.length) {
    return base;
  }
  const ids = new Set(base.map(entry => entry.id));
  const out = [...base];
  for (const entry of existing) {
    if (entry.builtin || ids.has(entry.id)) {
      continue;
    }
    out.push({
      ...entry,
      builtin: false,
      category: entry.category || "user",
      preset: cloneWavetablePreset(entry.preset),
      ...(entry.tags ? { tags: [...entry.tags] } : {}),
    });
    ids.add(entry.id);
  }
  return out;
}
